import React from 'react';
import './index.scss';
import { Helmet } from 'react-helmet';
import {
  PARTNERS_TITLE_EN,
  PARTNERS_DESC_EN,
  PARTNERS_TITLE_ES,
  PARTNERS_DESC_ES
} from '../../constants/defaults';
import { useTranslation } from 'react-i18next';
import _ from 'lodash';
import SkipTo from '../common/Tag/SkipTo';
import { LazyLoadImage } from 'react-lazy-load-image-component';

const Partners = () => {
  const { t, i18n } = useTranslation();
  let isEs = _.isEqual(i18n.language, 'es');

  return (
    <main>
      <div className="partnerLanderSection">
        <Helmet>
          <title>{isEs ? PARTNERS_TITLE_ES : PARTNERS_TITLE_EN}</title>
          <meta
            name="description"
            content={isEs ? PARTNERS_DESC_ES : PARTNERS_DESC_EN}
          />
          {/* <script type="application/ld+json">{getMainStoreLdJson()}</script> */}
          <link
            rel="canonical"
            href={`https://www.singlecare.com${
              !isEs ? '/partners' : '/es/socios'
            }`}
          />
          <link
            rel="alternate"
            href="https://www.singlecare.com/partners"
            hrefLang="en-us"
          />
          <link
            rel="alternate"
            href="https://www.singlecare.com/es/socios"
            hrefLang="es-us"
          />
          <meta
            property="og:title"
            content={isEs ? PARTNERS_TITLE_ES : PARTNERS_TITLE_EN}
          />
          <meta
            property="og:description"
            content={isEs ? PARTNERS_DESC_ES : PARTNERS_DESC_EN}
          />
          <meta
            property="og:url"
            content={`https://www.singlecare.com${
              !isEs ? '/partners' : '/es/socios'
            }`}
          />
          <meta property="og:site_name" content="SingleCare" />
          <meta property="og:type" content="website" />
          <meta
            property="og:image"
            content={
              'https://www.singlecare.com/content/images/homepage/singleCareLogo.jpg'
            }
          />
        </Helmet>
        <div className="partnerLanderContent">
          <div className="topSection">
            <SkipTo>
              <h1>
                {t('SingleCare cares about our communities')}
                <span className="icon"></span>
              </h1>
            </SkipTo>
            <p>
              {t(
                'SingleCare believes everyone should be able to afford their medications, regardless of insurance status.'
              )}
            </p>
            <p>
              {t(
                'To advance its mission, SingleCare partners with nonprofit organizations to help local communities access affordable prescription medications, ensuring greater health and financial stability.'
              )}
            </p>
            <p className="paraText">
              {t(
                'By providing our nonprofit partners with free discount cards, SingleCare enriches partner programs and their clients.'
              )}
            </p>
          </div>
          <div className="partnerLogos">
            <ul>
              <li>
                <LazyLoadImage
                  src="/content/images/partnerLander/american-heart-association.png"
                  width="133"
                  height="80"
                  alt={t('logos-partner-heart')}
                />
              </li>
              <li>
                <LazyLoadImage
                  src="/content/images/partnerLander/ala-stacked-logo.png"
                  width="139"
                  height="57"
                  alt=""
                />
              </li>
              <li>
                <LazyLoadImage
                  src="/content/images/partnerLander/mha-logo.png"
                  width="140"
                  height="43"
                  alt={t('Mental Health America logo')}
                />
              </li>
            </ul>
          </div>
          <div className="partnerContact">
            <p>
              {t(
                'If you’re interested in partnering with SingleCare, please contact us at'
              )}
              <br />
              <a href="mailto:partners@singlecare.com">
                partners@singlecare.com
              </a>
              .
            </p>
            <a
              href="mailto:partners@singlecare.com"
              className="pink button contactUs"
            >
              {t('Contact us')}
            </a>
          </div>
          <div className="whyPatnerSection">
            <h2>
              {t('Why partner with')} <br /> SingleCare?
            </h2>
            <p>
              {t(
                'SingleCare is a free service that helps people save up to 80% on their medications.'
              )}{' '}
              <br />
              {t(
                'To date, we have saved families and individuals more than $4 billion.'
              )}
            </p>
            <p>
              {t(
                'By partnering together, we can wrap around your current programming, complementing'
              )}
            </p>
            <ul>
              <li>
                <a href="">{t('Job training programs')}</a>
              </li>
              <li>
                <a href="">{t('Healthcare initiatives')}</a>
              </li>
              <li>
                <a href="">{t('Tax and financial assistance programs')}</a>
              </li>
              <li>
                <a href="">{t('Employer benefit programs')}</a>
              </li>
            </ul>
          </div>
          <div className="aboutSinglecare">
            <h2>{t('What our nonprofit partners say about SingleCare')}</h2>
            <div className="aboutData borderBottom-0">
              <h3>American Lung Association</h3>
              <p>
                "
                {t(
                  'The mission of the American Lung Association, to save lives by improving lung health and preventing lung disease, is strengthened by the partnership we have with FamilyWize, now SingleCare. Individuals living with chronic lung disease may need assistance accessing the medications required to manage their disease, and we appreciate the ability to share resources on Lung.org and through our Lung HelpLine (1-800-LUNG-USA) to help individuals breathe easier.'
                )}
                "
              </p>
              <span className="userName">
                Bev Stewart,
                {t('National Senior Director')}, Health Promotions, Lung
                Disease, American Lung Association
              </span>
            </div>
          </div>

          <div className="corporateGiving">
            <h2>{t('Corporate giving through RxSense')}</h2>
            <p>
              {t(
                'SingleCare, an RxSense company, supports our local communities by giving to mission-driven organizations across the country.'
              )}
            </p>
            <ul>
              <li>
                <LazyLoadImage
                  src="/content/images/partnerLander/the-goodness-web.png"
                  width="278"
                  height="56"
                  alt=""
                />
              </li>
              <li>
                <LazyLoadImage
                  src="/content/images/partnerLander/CAP_Logo.png"
                  width="212"
                  height="74"
                  alt=""
                />
              </li>
              <li>
                <LazyLoadImage
                  src="/content/images/partnerLander/suits-for-seniors-log.png"
                  width="107"
                  height="110"
                  alt=""
                />
              </li>
              <li>
                <LazyLoadImage
                  src="/content/images/partnerLander/hh-logo.png"
                  width="220"
                  height="66"
                  alt=""
                />
              </li>
            </ul>
          </div>

          <div className="interestedSectiom">
            <p>
              {t(
                'If you’re interested in partnering with SingleCare and helping the communities you serve save on their prescription medications, please contact us at'
              )}{' '}
              <br />
              <a href="mailto:partners@singlecare.com">
                partners@singlecare.com
              </a>
              .
            </p>
            <a
              href="mailto:partners@singlecare.com"
              className="pink button contactUs"
            >
              {t('Contact us')}
            </a>
          </div>
        </div>
      </div>
    </main>
  );
};

export default Partners;
